import { useSelector, useDispatch } from 'react-redux';
import { Redux } from 'stores/interfaces';
import * as middleware from './middleware';
import {
	CustomerAccess,
	CustomerProvider,
	CustomerRestrictionTypes,
	HandleGetCustomerDataParams,
	HandleGetCustomerDataReturn,
	PermissionGateProps,
	PermissionIds,
} from './interfaces';
import { CustomerDataResponse } from 'services/customer/interfaces';
import { NavigateOptions, useLocation, useNavigate } from 'react-router-dom';
import { customerPermissions } from './helpers';
import { useEffect } from 'react';
import useElasticApm from 'contexts/ApmElastic';

const prevent2FARoutes = ['/acesso-distribuidor', '/login'];
const quotationProviders = ['zecacopiloto', 'zeca-pdv', 'televendas'];

export default function useCustomer() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { state, pathname, search } = useLocation();

	const customerStore = useSelector((stores: Redux) => stores.customerReducer);
	const searchParams = Object.fromEntries(new URLSearchParams(search));
	const posStore = useSelector((stores: Redux) => stores.posReducer);

	const { setAPMUser } = useElasticApm();

	const getRestrictionType = (): CustomerRestrictionTypes | undefined => {
		const fingerprint = customerStore.deviceToken;
		const provider = customerStore.provider || new URLSearchParams(search).get('origin');

		if (!provider) return undefined;

		if (quotationProviders.includes(provider)) return 'QUOTATION';

		if (!fingerprint && !prevent2FARoutes.includes(location.pathname)) {
			return '2FA_EXPIRED';
		}

		if (provider && provider !== 'PARCEIRO_HYPERA') return 'PARTNER';

		if (!!simulatedViewType()) return 'SIMULATED';
		if (!posStore?.myPos?.length) return 'NO_LINKS';

		return undefined;
	};

	const customerRestrictionRedirect: Record<CustomerRestrictionTypes, string> = {
		PARTNER: '/acesso-distribuidor',
		NO_LINKS: '/primeiro-acesso',
		QUOTATION: '/login',
		SIMULATED: '/simular-farmacia',
		'2FA_EXPIRED': '/verificacao-de-identidade',
	};

	const mainLinkedPosRedirect = posStore.selectedPos?.id ? '/' : '/minhas-farmacias';

	const defaultCustomerPage = () => {
		const restrictionType = getRestrictionType();
		return restrictionType ? customerRestrictionRedirect[restrictionType] : mainLinkedPosRedirect;
	};

	const handleGoToInitialPage = (options?: NavigateOptions) => {
		return navigate(defaultCustomerPage(), { state, ...options });
	};

	const handleManageFirstAccess = {
		get(posId: string) {
			return customerStore.firstAccess?.includes(posId);
		},
		remove(posId: string) {
			dispatch(middleware.handleManageFirstAccess('REMOVE', posId));
		},
		add(posId: string) {
			dispatch(middleware.handleManageFirstAccess('ADD', posId));
		},
		clear(posId: string) {
			dispatch(middleware.handleManageFirstAccess('CLEAR', posId));
		},
	};

	// SIMULATED HANDLERS

	const handleHasPermission = (id: PermissionIds) => {
		const { accessType } = customerStore.data;

		if (accessType === CustomerAccess.BASIC) return false;
		if (accessType === CustomerAccess.ACTION) return customerPermissions.simulated.action.includes(id as any);

		return true;
	};

	const simulatedViewType = () => {
		const { accessType } = customerStore.data;
		if (accessType) return accessType;

		return false;
	};

	const PermissionGate = ({ children, id }: PermissionGateProps) => (handleHasPermission(id) ? children : null);
	const mainCustomerPage = () => defaultCustomerPage();

	useEffect(() => {
		setAPMUser({ id: customerStore.data.id, email: customerStore.data.email, name: customerStore.data.name });
	}, [customerStore.data.id]);

	return {
		customerStore,
		handleManageFirstAccess,
		customerRestrictionRedirect,

		handleSetLoading: (loading: boolean) => dispatch(middleware.handleSetLoading(loading)),
		handleResetUserIsLoggingOut: () => dispatch(middleware.handleResetUserIsLoggingOut()),
		handleGetCustomerData: (props?: HandleGetCustomerDataParams): Promise<HandleGetCustomerDataReturn> =>
			dispatch(middleware.handleGetCustomerData(props)),
		handleSetLogout: (userIsLoggingOut?: boolean) => dispatch(middleware.handleSetLogout(userIsLoggingOut)),
		handleSetToken: (token: string, provider: CustomerProvider = 'PARCEIRO_HYPERA') => {
			dispatch(middleware.handleSetToken(token));
			dispatch(middleware.handleSetProvider(provider));
		},
		handleSetProvider: (provider: CustomerProvider) => dispatch(middleware.handleSetProvider(provider)),
		handleUpdatePartially: (customerData: Partial<CustomerDataResponse>) =>
			dispatch(middleware.handleUpdatePartially(customerData)),
		handleUpdate2FAExpiration: (timestamp: number) => dispatch(middleware.handleUpdate2FAExpiration(timestamp)),
		handleUpdateCurrentStep: (step: number) => dispatch(middleware.handleUpdateCurrentStep(step)),
		handleGoToInitialPage,
		handleHasPermission,
		PermissionGate,
		simulatedViewType,
		mainCustomerPage,
		getRestrictionType,
		handleSetPartnerLabel: (label: string) => dispatch(middleware.handleSetPartnerLabel(label)),
		handleSetDeviceToken: (deviceToken?: string) => dispatch(middleware.handleSetDeviceToken(deviceToken)),
		isPartner() {
			const restriction = getRestrictionType();

			if (['PARCEIRO_HYPERA', ...quotationProviders].includes(customerStore.provider || '')) return false;

			return restriction === 'PARTNER' || !!customerStore.provider;
		},
	};
}
